import { useState, useEffect } from 'react';
import {
  Page,
  Download,
  RefreshDouble,
  Search,
  Calendar,
  User as UserIcon,
  Check,
  Xmark,
  WarningTriangle,
} from 'iconoir-react';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const API_URL = 'http://localhost:8000/api/v1';

interface AuditLog {
  id: string;
  action: string;
  resource: string;
  resourceId?: string;
  actorId: string;
  actorType: string;
  actorEmail?: string;
  changes?: any;
  metadata?: any;
  ipAddress?: string;
  userAgent?: string;
  status: string;
  error?: string;
  createdAt: string;
  organizationId: string;
}

interface AuditStats {
  total: number;
  last24h: number;
  last7days: number;
}

export function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAction, setSelectedAction] = useState<string>('all');
  const [selectedResource, setSelectedResource] = useState<string>('all');
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [searchQuery, selectedAction, selectedResource]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (selectedAction !== 'all') params.append('action', selectedAction);
      if (selectedResource !== 'all') params.append('resource', selectedResource);
      
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/audit?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/audit/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      
      setLogs(logsRes.data.data || []);
      setStats(statsRes.data.data || { total: 0, last24h: 0, last7days: 0 });
    } catch (error: any) {
      console.error('Error loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportLogs = (format: 'csv' | 'json') => {
    const dataStr = format === 'json' 
      ? JSON.stringify(logs, null, 2)
      : convertToCSV(logs);
    
    const blob = new Blob([dataStr], { type: format === 'json' ? 'application/json' : 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-logs-${dayjs().format('YYYY-MM-DD')}.${format}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const convertToCSV = (data: AuditLog[]) => {
    const headers = ['Date', 'Action', 'Resource', 'Actor', 'Status', 'IP Address'];
    const rows = data.map(log => [
      dayjs(log.createdAt).format('YYYY-MM-DD HH:mm:ss'),
      log.action,
      log.resource,
      log.actorEmail || log.actorId,
      log.status,
      log.ipAddress || '-',
    ]);
    
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const getActionColor = (action: string) => {
    if (action.includes('create')) return 'text-green-600 bg-green-50';
    if (action.includes('update')) return 'text-blue-600 bg-blue-50';
    if (action.includes('delete')) return 'text-red-600 bg-red-50';
    if (action.includes('generate')) return 'text-purple-600 bg-purple-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getActionIcon = (log: AuditLog) => {
    if (log.status === 'failure') return <Xmark className="w-4 h-4 text-red-500" />;
    if (log.status === 'success') return <Check className="w-4 h-4 text-green-500" />;
    return <WarningTriangle className="w-4 h-4 text-yellow-500" />;
  };

  // Group logs by date
  const groupedLogs = logs.reduce((acc, log) => {
    const date = dayjs(log.createdAt).format('YYYY-MM-DD');
    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {} as Record<string, AuditLog[]>);

  const filteredLogs = Object.keys(groupedLogs).sort((a, b) => b.localeCompare(a));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 shadow-lg">
              <Page className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
              <p className="text-sm text-gray-600">Track all actions and changes</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => exportLogs('csv')}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              CSV
            </button>
            <button
              onClick={() => exportLogs('json')}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              JSON
            </button>
            <button
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <RefreshDouble className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 shadow-md">
              <p className="text-sm text-gray-500 mb-1">Total Logs</p>
              <p className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">
                {stats.total}
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md">
              <p className="text-sm text-gray-500 mb-1">Last 24 Hours</p>
              <p className="text-2xl font-bold bg-gradient-to-r from-green-500 to-green-600 bg-clip-text text-transparent">
                {stats.last24h}
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md">
              <p className="text-sm text-gray-500 mb-1">Last 7 Days</p>
              <p className="text-2xl font-bold bg-gradient-to-r from-purple-500 to-purple-600 bg-clip-text text-transparent">
                {stats.last7days}
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search logs..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Actions</option>
              <option value="user.created">User Created</option>
              <option value="user.updated">User Updated</option>
              <option value="user.deleted">User Deleted</option>
              <option value="api_key.generated">API Key Generated</option>
              <option value="api_key.revoked">API Key Revoked</option>
              <option value="session.created">Session Created</option>
              <option value="session.revoked">Session Revoked</option>
            </select>
            <select
              value={selectedResource}
              onChange={(e) => setSelectedResource(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Resources</option>
              <option value="user">User</option>
              <option value="api_key">API Key</option>
              <option value="session">Session</option>
              <option value="organization">Organization</option>
            </select>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-indigo-500 border-t-transparent"></div>
              <p className="mt-4 text-gray-600">Loading audit logs...</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="p-12 text-center">
              <Page className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No audit logs found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredLogs.map((date) => (
                <div key={date} className="p-6">
                  {/* Date Header */}
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {dayjs(date).format('MMMM D, YYYY')}
                    </h3>
                    <span className="text-sm text-gray-500">
                      ({groupedLogs[date].length} events)
                    </span>
                  </div>

                  {/* Log Entries */}
                  <div className="space-y-3 ml-7">
                    {groupedLogs[date].map((log) => (
                      <div key={log.id} className="border-l-4 border-indigo-200 pl-4">
                        <div
                          className="cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
                          onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {getActionIcon(log)}
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                                  {log.action}
                                </span>
                                <span className="text-sm text-gray-500">
                                  {dayjs(log.createdAt).format('HH:mm:ss')}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <UserIcon className="w-4 h-4" />
                                <span>{log.actorEmail || log.actorId}</span>
                                {log.ipAddress && (
                                  <>
                                    <span>•</span>
                                    <span>{log.ipAddress}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Expandable Details */}
                          {expandedLog === log.id && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <dl className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <dt className="font-medium text-gray-500">Resource</dt>
                                  <dd className="text-gray-900">{log.resource}</dd>
                                </div>
                                {log.resourceId && (
                                  <div>
                                    <dt className="font-medium text-gray-500">Resource ID</dt>
                                    <dd className="text-gray-900 font-mono text-xs">{log.resourceId}</dd>
                                  </div>
                                )}
                                <div>
                                  <dt className="font-medium text-gray-500">Status</dt>
                                  <dd>
                                    <span className={`px-2 py-1 rounded-full text-xs ${
                                      log.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                    }`}>
                                      {log.status}
                                    </span>
                                  </dd>
                                </div>
                                {log.userAgent && (
                                  <div className="col-span-2">
                                    <dt className="font-medium text-gray-500">User Agent</dt>
                                    <dd className="text-gray-900 text-xs">{log.userAgent}</dd>
                                  </div>
                                )}
                                {log.changes && (
                                  <div className="col-span-2">
                                    <dt className="font-medium text-gray-500">Changes</dt>
                                    <dd className="text-gray-900">
                                      <pre className="mt-1 bg-gray-50 p-2 rounded text-xs overflow-auto">
                                        {JSON.stringify(log.changes, null, 2)}
                                      </pre>
                                    </dd>
                                  </div>
                                )}
                                {log.error && (
                                  <div className="col-span-2">
                                    <dt className="font-medium text-red-500">Error</dt>
                                    <dd className="text-red-700 text-xs">{log.error}</dd>
                                  </div>
                                )}
                              </dl>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuditLogsPage;
